import React, { useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import image3  from '../images/chocolate.jpg';
import image8  from '../images/winebottle.jpg';
import image2  from '../images/wineone.jpg';

/* ─── Fonts ──────────────────────────────────────────────────── */
const FontLoader = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Bebas+Neue&family=Jost:wght@300;400;500;600&display=swap');
`;

/* ─── Tokens ─────────────────────────────────────────────────── */
const gold    = '#c9a96e';
const cream   = '#f5f0e8';
const ink     = '#0e0c0a';
const mist    = '#1a1712';
const warm    = '#f9f5ee';
const bodyClr = 'rgba(40,32,24,0.72)';
const display = `'Cormorant Garamond', Georgia, serif`;
const sans    = `'Jost', sans-serif`;
const slab    = `'Bebas Neue', sans-serif`;

/* ─── Keyframes ──────────────────────────────────────────────── */
const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

/* ─── Motion Variants ────────────────────────────────────────── */
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] } },
};

/* ─── Package Data ───────────────────────────────────────────── */
const PACKAGES = [
  {
    id: 1,
    label: 'Wine Tours',
    price: '$65 / person *TRANSPORTATION ONLY INCLUDED IN PRICE*',
    unit: 'This price does not include anything at the wineries',
    src: 'https://i.postimg.cc/j5LqF0MP/77153342-c177-46f9-9117-ee29a00617a7.png',
    desc: 'Explore the best local vineyards with our shuttle service. Choose any three wineries from our curated list of eight. Each stop is 1–1.5 hours. We handle all the driving.',
    highlight: true,
  },
  {
    id: 2,
    label: 'Wine Tastings',
    price: 'from $30',
    unit: 'per person',
    tag: 'New',
    src: 'https://i.postimg.cc/FzFr9Zrx/IMG-8188.jpg',
    desc: "You can't experience the Yadkin Valley without tasting the wine. We now offer wine tastings right here at the bed and breakfast — the perfect introduction to NC wine country.",
  },
  {
    id: 3,
    label: 'Bottle of Wine',
    price: '$20 – $125',
    unit: 'per bottle',
    tag: 'Always Available',
    src: 'https://i.postimg.cc/Y9H2P7Ty/IMG-6784.jpg',
    desc: 'Whatever your taste, we have it. Enjoy a beautiful bottle of wine with sweeping vineyard views, exceptional food, and great company.',
  },
  {
    id: 4,
    label: 'Charcuterie Board',
    price: '$25',
    unit: 'per board',
    tag: 'Crowd Favorite',
    src: 'https://i.postimg.cc/NFrVW6zB/littleheavenbasket.jpg',
    desc: 'Indulge in a curated charcuterie box loaded with premium cheeses and deli meats — perfect to enjoy solo or share to satisfy the afternoon hunger.',
  },
  {
    id: 5,
    label: 'Custom Cake',
    price: '$35',
    unit: 'per cake',
    tag: 'Celebrate',
    src: 'https://i.postimg.cc/NM2pMg7x/littleheavencafe.jpg',
    desc: 'Celebrate any occasion with a delicious custom cake of your choice. Whether it\'s a birthday, anniversary, or just a reason to treat yourself — this one\'s for you.',
  },
  {
    id: 6,
    label: 'Chocolate Strawberries',
    price: '$39.55',
    unit: 'per dozen',
    tag: 'Romantic',
    src: image3,
    desc: 'A dozen of our luscious strawberries hand-dipped in the chocolate of your choice. The perfect indulgence for a romantic getaway or a sweet treat any time.',
  },
  {
    id: 7,
    label: 'Seasonal Flowers',
    price: '$45.99',
    unit: 'per arrangement',
    tag: 'Gift',
    src: 'https://i.postimg.cc/yYsj99jP/IMG-8297.jpg',
    desc: 'Give that touch of love that only a beautiful bouquet of fresh seasonal flowers can bring. A thoughtful surprise for someone special — or yourself.',
  },
];

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
const Cards = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <>
      <FontLoader />
      <PageWrapper>

        {/* ── Masthead ──────────────────────────────────────── */}
        <Masthead>
          <MastheadNoise />
          <MastheadGlow />
          <MastheadInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            animate="show"
          >
            <EyebrowRow as={motion.div} variants={fadeUp}>
              <EyebrowLine />
              <Eyebrow>Little Heaven Bed &amp; Breakfast</Eyebrow>
              <EyebrowLine $flipped />
            </EyebrowRow>
            <MastheadTitle as={motion.div} variants={fadeUp}>
              Specials &amp;<br /><em>Packages</em>
            </MastheadTitle>
            <MastheadSub as={motion.div} variants={fadeUp}>
              Curated experiences to elevate every stay
            </MastheadSub>
          </MastheadInner>
        </Masthead>

        {/* ── Intro ─────────────────────────────────────────── */}
        <IntroSection>
          <IntroInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <IntroEyebrow as={motion.div} variants={fadeUp}>
              Thoughtfully Curated
            </IntroEyebrow>
            <IntroTitle as={motion.div} variants={fadeUp}>
              Everything You Need<br /><em>for the Perfect Stay</em>
            </IntroTitle>
            <IntroPara as={motion.div} variants={fadeUp}>
              Little Heaven offers a collection of add-ons and experiences designed
              to make your stay extraordinary. From wine tours through the Yadkin Valley
              to handcrafted boards, blooms, and bottles — every detail is taken care of.
            </IntroPara>
          </IntroInner>
        </IntroSection>

        {/* ── Featured Package (Wine Tour) ──────────────────── */}
        <FeaturedSection>
          <FeaturedInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <FeaturedImg as={motion.div} variants={fadeUp}>
              <img src={PACKAGES[0].src} alt={PACKAGES[0].label} />
              <FeaturedImgOverlay />
            </FeaturedImg>

            <FeaturedText as={motion.div} variants={stagger}>
              <FeaturedEyebrow as={motion.div} variants={fadeUp}>
                Signature Experience
              </FeaturedEyebrow>
              <FeaturedTitle as={motion.div} variants={fadeUp}>
                {PACKAGES[0].label}
              </FeaturedTitle>
              <FeaturedPrice as={motion.div} variants={fadeUp}>
                <PriceNum>{PACKAGES[0].price}</PriceNum>
                <PriceUnit>{PACKAGES[0].unit}</PriceUnit>
              </FeaturedPrice>
              <FeaturedRule as={motion.div} variants={fadeUp} />
              <FeaturedDesc as={motion.div} variants={fadeUp}>
                {PACKAGES[0].desc}
              </FeaturedDesc>
              <FeaturedFeatures as={motion.div} variants={stagger}>
                {[
                  'Choose any 3 from 10 local wineries',
                  '1–1.5 hours at each stop',
                  'Door-to-door shuttle service',   
                ].map((f) => (
                  <FeaturedFeature key={f} as={motion.div} variants={fadeUp}>
                    <FeatureDot />
                    {f}
                  </FeaturedFeature>
                ))}
              </FeaturedFeatures>
            </FeaturedText>
          </FeaturedInner>
        </FeaturedSection>

        {/* ── All Packages Grid ─────────────────────────────── */}
        <GridSection>
          <GridHeader
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <GridEyebrow as={motion.div} variants={fadeUp}>All Packages</GridEyebrow>
            <GridTitle as={motion.div} variants={fadeUp}>
              Add Something<br /><em>Special</em>
            </GridTitle>
          </GridHeader>

          <PackagesGrid
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
          >
            {PACKAGES.slice(1).map((pkg) => (
              <PackageCard
                key={pkg.id}
                as={motion.div}
                variants={fadeUp}
                whileHover="hover"
                onHoverStart={() => setHovered(pkg.id)}
                onHoverEnd={() => setHovered(null)}
              >
                <PackageImgWrap>
                  <PackageImg src={pkg.src} alt={pkg.label} />
                  <PackageImgOverlay
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    variants={{ hover: { opacity: 1, transition: { duration: 0.35 } } }}
                  />
                  <PackageTagPill>{pkg.tag}</PackageTagPill>
                </PackageImgWrap>

                <PackageBody>
                  <PackageLabelRow>
                    <PackageLabel>{pkg.label}</PackageLabel>
                    <PackagePriceChip>
                      <PackagePriceVal>{pkg.price}</PackagePriceVal>
                      <PackagePriceUnit>{pkg.unit}</PackagePriceUnit>
                    </PackagePriceChip>
                  </PackageLabelRow>

                  <PackageRule
                    as={motion.div}
                    variants={{ hover: { width: '100%', transition: { duration: 0.4 } } }}
                  />

                  <PackageDesc
                    as={motion.div}
                    initial={{ opacity: 0.65 }}
                    variants={{ hover: { opacity: 1, transition: { duration: 0.3 } } }}
                  >
                    {pkg.desc}
                  </PackageDesc>
                </PackageBody>
              </PackageCard>
            ))}
          </PackagesGrid>
        </GridSection>

        {/* ── CTA Strip ─────────────────────────────────────── */}
        <CTASection>
          <CTANoise />
          <CTAGlow />
          <CTAInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <CTAEyebrow as={motion.div} variants={fadeUp}>Ready to Book?</CTAEyebrow>
            <CTATitle as={motion.div} variants={fadeUp}>
              Add a Package<br /><em>to Your Stay</em>
            </CTATitle>
            <CTASub as={motion.div} variants={fadeUp}>
              Contact us when booking your room and we'll have everything
              ready and waiting for your arrival.
            </CTASub>
            <CTAButtons as={motion.div} variants={fadeUp}>
              <CTABtn
                href="tel:+13364682300"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.25 }}
              >
                Call Us · (336) 468-2300
              </CTABtn>
              <CTABtnOutline
                href="mailto:info@littleheavenbnb.com"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.25 }}
              >
                Send an Email ↗
              </CTABtnOutline>
            </CTAButtons>
          </CTAInner>
        </CTASection>

        {/* ── Marquee ───────────────────────────────────────── */}
        <MarqueeStrip>
          <MarqueeInner>
            {Array(7).fill('LITTLE HEAVEN · HAMPTONVILLE, NC · WINE TOURS · PACKAGES · SPECIALS ·').map((t, i) => (
              <MarqueeText key={i}>{t}</MarqueeText>
            ))}
          </MarqueeInner>
        </MarqueeStrip>

      </PageWrapper>
    </>
  );
};

export default Cards;

/* ═══════════════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════════════ */

const PageWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
  background: ${warm};
  color: ${ink};
  font-family: ${sans};
`;

/* ── Masthead ─────────────────────────────────────────────────── */
const Masthead = styled.section`
  position: relative;
  min-height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #141210 0%, #1e1a14 55%, #0e0c0a 100%);
  overflow: hidden;
  padding: 80px 24px 90px;
`;

const MastheadNoise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.025;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px;
  pointer-events: none;
`;

const MastheadGlow = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 65% 55% at 25% 40%, rgba(201,169,110,0.1) 0%, transparent 60%),
    radial-gradient(ellipse 45% 45% at 78% 60%, rgba(201,169,110,0.06) 0%, transparent 60%);
  pointer-events: none;
`;

const MastheadInner = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 860px;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 36px;
`;

const EyebrowLine = styled.div`
  flex: 1;
  max-width: 90px;
  height: 1px;
  background: ${(p) => p.$flipped
    ? `linear-gradient(90deg, ${gold}, transparent)`
    : `linear-gradient(90deg, transparent, ${gold})`};
`;

const Eyebrow = styled.span`
  font-family: ${sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  white-space: nowrap;
`;

const MastheadTitle = styled.h1`
  font-family: ${display};
  font-size: clamp(5rem, 13vw, 11rem);
  font-weight: 300;
  line-height: 0.92;
  color: ${cream};
  margin: 0 0 32px;
  letter-spacing: -3px;

  em {
    font-style: italic;
    color: ${gold};
    display: block;
    font-size: 0.9em;
  }
`;

const MastheadSub = styled.p`
  font-family: ${sans};
  font-size: 0.85rem;
  font-weight: 300;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(245,240,232,0.5);
  margin: 0;
`;

/* ── Intro ────────────────────────────────────────────────────── */
const IntroSection = styled.section`
  background: ${warm};
  padding: 100px 40px 80px;

  @media (max-width: 768px) { padding: 72px 24px 60px; }
`;

const IntroInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const IntroEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 14px;

  &::before {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.5;
  }
`;

const IntroTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(2.8rem, 5.5vw, 5rem);
  font-weight: 300;
  line-height: 1.05;
  color: ${ink};
  margin: 0 0 28px;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const IntroPara = styled.p`
  font-family: ${sans};
  font-size: 1.05rem;
  font-weight: 300;
  line-height: 1.95;
  color: ${bodyClr};
  margin: 0;
`;

/* ── Featured ─────────────────────────────────────────────────── */
const FeaturedSection = styled.section`
  background: linear-gradient(160deg, #141210 0%, #0e0c0a 100%);
  padding: 100px 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 60% 50% at 60% 50%, rgba(201,169,110,0.07) 0%, transparent 65%);
  }

  @media (max-width: 768px) { padding: 80px 24px; }
`;

const FeaturedInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const FeaturedImg = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 4/3;
  border: 1px solid rgba(201,169,110,0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover img { transform: scale(1.05); }
`;

const FeaturedImgOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(14,12,10,0.6) 100%);
  pointer-events: none;
`;

const FeaturedTagBadge = styled.div`
  position: absolute;
  top: 18px;
  left: 18px;
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${ink};
  background: ${gold};
  padding: 7px 16px;
  border-radius: 100px;
`;

const FeaturedText = styled.div``;

const FeaturedEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 14px;

  &::before {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.5;
  }
`;

const FeaturedTitle = styled.h2`
  font-family: ${slab};
  font-size: clamp(3.5rem, 6vw, 5.5rem);
  line-height: 1;
  letter-spacing: 2px;
  color: ${cream};
  margin: 0 0 24px;
`;

const FeaturedPrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 28px;
`;

const PriceNum = styled.div`
  font-family: ${display};
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 300;
  color: ${gold};
  line-height: 1;
`;

const PriceUnit = styled.div`
  font-family: ${sans};
  font-size: 0.85rem;
  font-weight: 300;
  color: rgba(245,240,232,0.5);
  letter-spacing: 1px;
`;

const FeaturedRule = styled.div`
  width: 56px;
  height: 2px;
  background: ${gold};
  opacity: 0.4;
  margin-bottom: 28px;
  border-radius: 2px;
`;

const FeaturedDesc = styled.p`
  font-family: ${sans};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.9;
  color: rgba(245,240,232,0.7);
  margin: 0 0 32px;
`;

const FeaturedFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeaturedFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${sans};
  font-size: 0.9rem;
  font-weight: 400;
  color: rgba(245,240,232,0.75);
`;

const FeatureDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${gold};
  flex-shrink: 0;
`;

/* ── Grid Section ─────────────────────────────────────────────── */
const GridSection = styled.section`
  background: ${warm};
  padding: 110px 40px 120px;

  @media (max-width: 768px) { padding: 80px 24px 90px; }
`;

const GridHeader = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const GridEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.5;
  }
`;

const GridTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 5.5vw, 5rem);
  font-weight: 300;
  line-height: 1.05;
  color: ${ink};
  margin: 0;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const PackagesGrid = styled.div`
  max-width: 1380px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px)  { grid-template-columns: 1fr; max-width: 460px; }
`;

const PackageCard = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(201,169,110,0.14);
  box-shadow: 0 4px 32px rgba(40,32,24,0.06);
  cursor: default;
  transition: border-color 0.35s, box-shadow 0.35s, transform 0.35s;

  &:hover {
    border-color: rgba(201,169,110,0.45);
    box-shadow: 0 16px 60px rgba(40,32,24,0.12);
    transform: translateY(-6px);
  }
`;

const PackageImgWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  overflow: hidden;
  background: #2a2420;
`;

const PackageImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);

  ${PackageCard}:hover & { transform: scale(1.06); }
`;

const PackageImgOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(14,12,10,0.65) 100%);
`;

const PackageTagPill = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  font-family: ${sans};
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${gold};
  padding: 5px 12px;
  background: rgba(14,12,10,0.75);
  border: 1px solid rgba(201,169,110,0.3);
  border-radius: 100px;
  backdrop-filter: blur(8px);
`;

const PackageBody = styled.div`
  padding: 28px 28px 32px;
`;

const PackageLabelRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const PackageLabel = styled.h3`
  font-family: ${slab};
  font-size: 1.7rem;
  letter-spacing: 1.5px;
  color: ${ink};
  margin: 0;
  line-height: 1;
  transition: color 0.3s;

  ${PackageCard}:hover & { color: ${gold}; }
`;

const PackagePriceChip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
`;

const PackagePriceVal = styled.div`
  font-family: ${display};
  font-size: 1.4rem;
  font-weight: 400;
  color: ${gold};
  line-height: 1;
`;

const PackagePriceUnit = styled.div`
  font-family: ${sans};
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(40,32,24,0.4);
  margin-top: 2px;
`;

const PackageRule = styled.div`
  width: 36px;
  height: 2px;
  background: ${gold};
  opacity: 0.35;
  margin-bottom: 16px;
  border-radius: 2px;
  transition: width 0.4s, opacity 0.4s;

  ${PackageCard}:hover & { width: 56px; opacity: 0.8; }
`;

const PackageDesc = styled.p`
  font-family: ${sans};
  font-size: 0.88rem;
  font-weight: 300;
  line-height: 1.8;
  color: ${bodyClr};
  margin: 0;
`;

/* ── CTA ──────────────────────────────────────────────────────── */
const CTASection = styled.section`
  position: relative;
  background: linear-gradient(160deg, #141210 0%, #0e0c0a 100%);
  padding: 120px 40px 130px;
  overflow: hidden;

  @media (max-width: 768px) { padding: 90px 24px 100px; }
`;

const CTANoise = styled(MastheadNoise)``;

const CTAGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(201,169,110,0.08) 0%, transparent 65%);
  pointer-events: none;
`;

const CTAInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const CTAEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.7);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.4;
  }
`;

const CTATitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 7vw, 6.5rem);
  font-weight: 300;
  line-height: 1.0;
  color: ${cream};
  margin: 0 0 28px;
  letter-spacing: -1px;

  em { font-style: italic; color: ${gold}; }
`;

const CTASub = styled.p`
  font-family: ${sans};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.9;
  color: rgba(245,240,232,0.6);
  margin: 0 0 48px;
`;

const CTAButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const CTABtn = styled(motion.a)`
  display: inline-block;
  font-family: ${sans};
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-decoration: none;
  color: ${ink};
  background: ${gold};
  padding: 16px 40px;
  border-radius: 100px;
  box-shadow: 0 8px 32px rgba(201,169,110,0.3);
  transition: background 0.3s;

  &:hover { background: #dbbf85; }
`;

const CTABtnOutline = styled(motion.a)`
  display: inline-block;
  font-family: ${sans};
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-decoration: none;
  color: ${gold};
  background: transparent;
  padding: 16px 40px;
  border-radius: 100px;
  border: 1px solid rgba(201,169,110,0.4);
  transition: background 0.3s, border-color 0.3s;

  &:hover {
    background: rgba(201,169,110,0.08);
    border-color: ${gold};
  }
`;

/* ── Marquee ──────────────────────────────────────────────────── */
const MarqueeStrip = styled.div`
  background: ${gold};
  padding: 13px 0;
  overflow: hidden;
  white-space: nowrap;
`;

const MarqueeInner = styled.div`
  display: inline-flex;
  animation: ${marquee} 26s linear infinite;
`;

const MarqueeText = styled.span`
  font-family: ${slab};
  font-size: 0.95rem;
  letter-spacing: 3px;
  color: ${ink};
  padding: 0 28px;
`;